import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ContextualLinkPanel } from "@/components/ContextualLinkPanel";

const resultDrivers = [
  {
    title: "Day-to-day fit",
    body: "How well a country fits the kind of life you want to live."
  },
  {
    title: "Long-term affordability",
    body: "Whether the cost of living looks realistic for your budget over time."
  },
  {
    title: "Healthcare and infrastructure",
    body: "How strong and dependable the basics are, including healthcare access and everyday convenience."
  },
  {
    title: "Residency practicality",
    body: "How realistic and workable the path to living there appears to be."
  }
];

const sections = [
  {
    title: "Why results differ from one person to another",
    paragraphs: [
      "Relocation Clarity is not built around a fixed list of “best countries.”",
      "Your shortlist changes based on what you tell us matters most. Someone focused on affordability and ease of transition may see different results than someone focused on healthcare, stability, or lifestyle fit."
    ]
  },
  {
    title: "What the assessment is doing",
    paragraphs: [
      "The assessment helps Relocation Clarity understand your priorities, tradeoffs, and practical constraints so it can narrow the countries most worth comparing.",
      "The more accurately you prioritize what matters most to you, the more useful and trustworthy your results will be."
    ]
  },
  {
    title: "How to use Relocation Clarity",
    paragraphs: [
      "Use Relocation Clarity to narrow the shortlist first, then go deeper on the countries that still fit.",
      "Relocation Clarity works best alongside your own research. It helps you focus your attention on the countries most worth comparing, then confirm important details through official sources and qualified professionals as needed."
    ]
  },
  {
    title: "What Relocation Clarity is not",
    paragraphs: [
      "Relocation Clarity is not legal, tax, immigration, or financial advice. It is designed to help you compare countries more clearly and make a more confident relocation decision before you commit."
    ]
  },
  {
    title: "Bottom line",
    paragraphs: [
      "Relocation Clarity helps you spend less time researching in circles and more time focusing on the countries that actually fit your life."
    ]
  }
];

export default function HowRelocationClarityWorks() {
  return (
    <div className="min-h-screen bg-[#f7f4ee] text-stone-900" data-testid="how-relocation-clarity-works-page">
      <Header />

      <main className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-5xl space-y-12">
          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-10 shadow-[0_18px_48px_rgba(28,25,23,0.06)] sm:px-10" data-testid="how-relocation-clarity-works-hero">
            <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" data-testid="how-relocation-clarity-works-hero-accent" />
            <p className="rc-section-kicker mb-4">How it works</p>
            <h1 className="font-manrope text-4xl text-stone-900 sm:text-5xl" data-testid="how-relocation-clarity-works-title">
              How Relocation Clarity Works
            </h1>
            <div className="mt-6 max-w-3xl space-y-5 text-base leading-8 text-stone-600" data-testid="how-relocation-clarity-works-intro">
              <p>
                Relocation Clarity is a decision-support platform that helps you compare countries for living abroad before you commit.
              </p>
              <p>
                It guides you through a simple process to narrow your options, weigh real-world tradeoffs, and focus on the countries that best fit your life.
              </p>
            </div>
          </section>

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="how-relocation-clarity-works-drivers-section">
            <h2 className="font-manrope text-2xl text-stone-900">What shapes your results</h2>
            <p className="mt-3 max-w-3xl text-base leading-8 text-stone-600">
              Your results are built around four core areas:
            </p>
            <div className="mt-8 grid gap-4 md:grid-cols-2" data-testid="how-relocation-clarity-works-drivers-grid">
              {resultDrivers.map((item, index) => (
                <article
                  key={item.title}
                  className="rounded-[1.5rem] border border-stone-200/80 bg-stone-50/80 p-5"
                  data-testid={`how-relocation-clarity-works-driver-${index + 1}`}
                >
                  <h3 className="text-base font-semibold text-stone-900">{item.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-stone-600">{item.body}</p>
                </article>
              ))}
            </div>
          </section>

          <div className="grid gap-6" data-testid="how-relocation-clarity-works-sections">
            {sections.map((section, index) => (
              <section
                key={section.title}
                className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10"
                data-testid={`how-relocation-clarity-works-section-${index + 1}`}
              >
                <h2 className="font-manrope text-2xl text-stone-900">{section.title}</h2>
                <div className="mt-4 space-y-4 text-base leading-8 text-stone-600">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <ContextualLinkPanel
            kicker="Useful next steps"
            title="Pick the next page that fits where you are in the decision."
            description="If you want more trust and detail, go to the FAQs. If you want proof of what the paid product looks like, open the Sample Blueprint. If you are ready to personalize the process, begin the assessment."
            testIdPrefix="how-relocation-clarity-works-next-steps"
            items={[
              {
                slug: "methodology",
                eyebrow: "More detail",
                title: "View Methodology & FAQs",
                description: "See how the methodology is framed, what the product does, and where its boundaries are.",
                ctaLabel: "Read the FAQs",
                to: "/methodology",
              },
              {
                slug: "sample-blueprint",
                eyebrow: "See the paid output",
                title: "Preview a Sample Blueprint",
                description: "See how results turn into a fuller Blueprint with tradeoffs, residency context, and next steps.",
                ctaLabel: "Open the sample",
                to: "/sample-plan",
              },
              {
                slug: "assessment",
                eyebrow: "Start here",
                title: "Begin your assessment",
                description: "Move from the general process into a shortlist built around your own priorities and constraints.",
                ctaLabel: "Start the assessment",
                to: "/assessment",
              },
            ]}
          />
        </div>
      </main>

      <Footer />
    </div>
  );
}